import React, { useEffect, useState, useMemo } from "react";
import {
  Plus,
  X,
  ChevronUp,
  ChevronDown,
  Trash2,
  Pencil,
  Eye,
  EyeOff,
} from "lucide-react";
import { getToken, base_url } from "../../config/config";

function UserTable() {
  // ------------------ State Initialization ------------------
  const [users, setUsers] = useState([]); // Initialize as empty array
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [openDialog, setOpenDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPreviousPassword, setShowPreviousPassword] = useState(false);
  const [previousPassword, setPreviousPassword] = useState("");

  // User state
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    userType: "user",
  });

  // For filtering columns
  const [columnFilters, setColumnFilters] = useState({
    firstName: "",
    lastName: "",
    email: "",
    userType: "",
  });

  const userTypeOptions = ["super-admin", "admin", "user"];

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const fetchData = async () => {
    try {
      const response = await fetch(`${base_url}/v1/GetAllAdmins`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      const data = await response.json();
      // The API returns data.users instead of data.data
      const userData = Array.isArray(data.message?.users) ? data.message.users : [];
      setUsers(userData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]); // Ensure users is set to an empty array on error
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, []);

  const handleSubmit = async () => {
    try {
      const response = await fetch(`${base_url}/v1/signUp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(newUser),
      });

      const data = await response.json();

      if (response.ok) {
        alert("Benutzer erfolgreich erstellt.");
        handleCloseDialog();
        fetchData();
      } else {
        alert(
          data.message ||
            "Ein Benutzer mit der gleichen E-Mail-Adresse existiert bereits."
        );
      }
    } catch (error) {
      console.error("Error creating user", error);
    }
  };

  const handleEditSubmit = async () => {
    try {
      const response = await fetch(
        `${base_url}/v1/UpdateUser?id=${selectedUser._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            firstName: selectedUser.firstName,
            lastName: selectedUser.lastName,
            email: selectedUser.email,
            userType: selectedUser.userType,
            // Only include password if it was changed, and include previous password for verification
            ...(selectedUser.password && { 
              password: selectedUser.password,
              previousPassword: previousPassword 
            }),
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        alert("Benutzer erfolgreich bearbeitet!");
        handleEditCloseDialog();
        fetchData();
      } else {
        alert(data.message || "Fehler beim Bearbeiten des Benutzers.");
      }
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewUser({
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      userType: "user",
    });
    setShowPassword(false);
    setShowPreviousPassword(false);
    setPreviousPassword("");
  };

  const editUser = (user) => {
    const userCopy = { ...user, password: "" }; // Don't include the existing password
    setSelectedUser(userCopy);
    setOpenDialog(true);
  };

  const addUser = () => {
    setSelectedUser(null);
    setOpenDialog(true);
  };

  const handleEditCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setShowPassword(false);
    setShowPreviousPassword(false);
    setPreviousPassword("");
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Möchten Sie diesen Benutzer wirklich löschen?")) {
      return;
    }

    try {
      const response = await fetch(`${base_url}/v1/DeleteUser?id=${userId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        alert("Benutzer erfolgreich gelöscht");
        fetchData();
      } else {
        alert(data.message || "Benutzer nicht gefunden.");
      }
    } catch (error) {
      console.error("Error deleting user", error);
    }
  };

  const getSortValue = (item, key) => {
    return item[key] || ""; // Add fallback for missing properties
  };

  const filteredData = useMemo(() => {
    // Ensure users is an array
    if (!Array.isArray(users)) {
      console.error("Expected users to be an array but got:", typeof users);
      return [];
    }

    let data = [...users]; // Create a copy to avoid mutations

    if (columnFilters.firstName) {
      data = data.filter((item) =>
        item?.firstName
          ?.toString()
          .toLowerCase()
          .includes(columnFilters.firstName.toLowerCase())
      );
    }

    if (columnFilters.lastName) {
      data = data.filter((item) =>
        item?.lastName
          ?.toString()
          .toLowerCase()
          .includes(columnFilters.lastName.toLowerCase())
      );
    }

    if (columnFilters.email) {
      data = data.filter((item) =>
        item?.email
          ?.toString()
          .toLowerCase()
          .includes(columnFilters.email.toLowerCase())
      );
    }

    if (columnFilters.userType) {
      data = data.filter((item) =>
        item?.userType
          ?.toString()
          .toLowerCase()
          .includes(columnFilters.userType.toLowerCase())
      );
    }

    if (sortConfig.key) {
      data = data.slice().sort((a, b) => {
        let aVal = getSortValue(a, sortConfig.key) || "";
        let bVal = getSortValue(b, sortConfig.key) || "";
        aVal = aVal.toString().toLowerCase();
        bVal = bVal.toString().toLowerCase();

        if (aVal < bVal) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return data; // Ensure we always return an array
  }, [users, columnFilters, sortConfig]);

  // Return an empty array if filteredData is not an array (as a fallback)
  const renderData = Array.isArray(filteredData) ? filteredData : [];

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <button
          onClick={addUser}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Benutzer hinzufügen
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                    onClick={() => requestSort("firstName")}
                  >
                    Vorname
                    {sortConfig.key === "firstName" &&
                      sortConfig.direction === "asc" && (
                        <ChevronUp size={16} className="inline-block ml-1" />
                      )}
                    {sortConfig.key === "firstName" &&
                      sortConfig.direction === "desc" && (
                        <ChevronDown size={16} className="inline-block ml-1" />
                      )}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                    onClick={() => requestSort("lastName")}
                  >
                    Nachname
                    {sortConfig.key === "lastName" &&
                      sortConfig.direction === "asc" && (
                        <ChevronUp size={16} className="inline-block ml-1" />
                      )}
                    {sortConfig.key === "lastName" &&
                      sortConfig.direction === "desc" && (
                        <ChevronDown size={16} className="inline-block ml-1" />
                      )}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                    onClick={() => requestSort("email")}
                  >
                    E-mail
                    {sortConfig.key === "email" &&
                      sortConfig.direction === "asc" && (
                        <ChevronUp size={16} className="inline-block ml-1" />
                      )}
                    {sortConfig.key === "email" &&
                      sortConfig.direction === "desc" && (
                        <ChevronDown size={16} className="inline-block ml-1" />
                      )}
                  </th>
                  <th
                    className="px-6 py-3 text-left text-sm font-semibold text-gray-900 cursor-pointer"
                    onClick={() => requestSort("userType")}
                  >
                    Benutzertyp
                    {sortConfig.key === "userType" &&
                      sortConfig.direction === "asc" && (
                        <ChevronUp size={16} className="inline-block ml-1" />
                      )}
                    {sortConfig.key === "userType" &&
                      sortConfig.direction === "desc" && (
                        <ChevronDown size={16} className="inline-block ml-1" />
                      )}
                  </th>
                  <th className="px-6 py-3 text-sm font-semibold text-gray-900">
                    Aktionen
                  </th>
                </tr>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-1">
                    <input
                      type="text"
                      placeholder="Suchen"
                      value={columnFilters.firstName}
                      onChange={(e) =>
                        setColumnFilters((prev) => ({
                          ...prev,
                          firstName: e.target.value,
                        }))
                      }
                      className="w-full text-sm border rounded"
                    />
                  </th>
                  <th className="px-6 py-1">
                    <input
                      type="text"
                      placeholder="Suchen"
                      value={columnFilters.lastName}
                      onChange={(e) =>
                        setColumnFilters((prev) => ({
                          ...prev,
                          lastName: e.target.value,
                        }))
                      }
                      className="w-full text-sm border rounded"
                    />
                  </th>
                  <th className="px-6 py-1">
                    <input
                      type="text"
                      placeholder="Suchen"
                      value={columnFilters.email}
                      onChange={(e) =>
                        setColumnFilters((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      className="w-full text-sm border rounded"
                    />
                  </th>
                  <th className="px-6 py-1">
                    <input
                      type="text"
                      placeholder="Suchen"
                      value={columnFilters.userType}
                      onChange={(e) =>
                        setColumnFilters((prev) => ({
                          ...prev,
                          userType: e.target.value,
                        }))
                      }
                      className="w-full text-sm border rounded"
                    />
                  </th>
                  <th className="px-6 py-1"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {renderData.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-2 text-sm text-gray-900">
                      {user.firstName}
                    </td>
                    <td className="px-6 py-2 text-sm text-gray-900">
                      {user.lastName}
                    </td>
                    <td className="px-6 py-2 text-sm text-gray-900">
                      {user.email}
                    </td>
                    <td className="px-6 py-2 text-sm text-gray-900">
                      {user.userType}
                    </td>
                    <td className="px-6 py-2">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => editUser(user)}
                          className="inline-flex items-center gap-1 rounded-lg bg-blue-50 px-3 py-1 text-sm font-medium text-blue-600 hover:bg-blue-100"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteUser(user._id)}
                          className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-3 py-1 text-sm font-medium text-red-600 hover:bg-red-100"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {openDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  {selectedUser ? "Benutzer bearbeiten" : "Benutzer hinzufügen"}
                </h2>
                <button
                  onClick={
                    selectedUser ? handleEditCloseDialog : handleCloseDialog
                  }
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Vorname
                  </label>
                  <input
                    type="text"
                    value={
                      selectedUser ? selectedUser.firstName : newUser.firstName
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      selectedUser
                        ? setSelectedUser({ ...selectedUser, firstName: value })
                        : setNewUser({ ...newUser, firstName: value });
                    }}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Nachname
                  </label>
                  <input
                    type="text"
                    value={
                      selectedUser ? selectedUser.lastName : newUser.lastName
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      selectedUser
                        ? setSelectedUser({ ...selectedUser, lastName: value })
                        : setNewUser({ ...newUser, lastName: value });
                    }}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  E-mail
                </label>
                <input
                  type="email"
                  value={selectedUser ? selectedUser.email : newUser.email}
                  onChange={(e) => {
                    const value = e.target.value;
                    selectedUser
                      ? setSelectedUser({ ...selectedUser, email: value })
                      : setNewUser({ ...newUser, email: value });
                  }}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                />
              </div>

              {/* Previous Password Field - Only show when editing user and changing password */}
              {selectedUser && selectedUser.password && (
                <div className="relative space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Aktuelles Passwort des Benutzers bestätigen
                  </label>
                  <div className="relative">
                    <input
                      type={showPreviousPassword ? "text" : "password"}
                      value={previousPassword}
                      onChange={(e) => setPreviousPassword(e.target.value)}
                      placeholder="Aktuelles Passwort des Benutzers eingeben"
                      autoComplete="current-password"
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 pr-10 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                      onClick={() => setShowPreviousPassword(!showPreviousPassword)}
                    >
                      {showPreviousPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
              )}

              <div className="relative space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  {selectedUser ? "Neues Passwort (leer lassen, um nicht zu ändern)" : "Passwort"}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={
                      selectedUser ? selectedUser.password : newUser.password
                    }
                    autoComplete="new-password"
                    onChange={(e) => {
                      const value = e.target.value;
                      selectedUser
                        ? setSelectedUser({ ...selectedUser, password: value })
                        : setNewUser({ ...newUser, password: value });
                    }}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 pr-10 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Benutzertyp
                </label>
                <select
                  value={
                    selectedUser ? selectedUser.userType : newUser.userType
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    selectedUser
                      ? setSelectedUser({ ...selectedUser, userType: value })
                      : setNewUser({ ...newUser, userType: value });
                  }}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                >
                  {userTypeOptions.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={
                  selectedUser ? handleEditCloseDialog : handleCloseDialog
                }
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
              >
                Abbrechen
              </button>
              <button
                onClick={selectedUser ? handleEditSubmit : handleSubmit}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserTable;
